import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Carousel } from "react-bootstrap";
import { RELOAD_TRUCK, Gallery1, Gallery2, Gallery3 } from "../../constants/app.constants";
import Tabs from "../../widgets/tabs";
import Table from "../../widgets/table";
import LoaderTrans from "../../widgets/loadertrans";
import { Card } from "../../widgets/card";
import ContactCardList from "../../widgets/contact_card_list";
import { ERROR_MESSAGE } from "../../constants/dialog.constants";
import ErrorCard from "../../widgets/error";
import {
  BENCHMARK_CONTACT,
  BENCHMARK_ORGANISATION,
  BENCHMARK_CONTACTLIST,
  BENCHMARK_INSTALLATIONLIST,
  BASE_URL,
} from "../../constants/app.constants";
import {
  InfoCard as BaseInfoCard,
  InfoCardType,
  LabelType,
} from "../../widgets/info_card";
import {
  fetchBenchmarkContact,
  fetchBenchmarkOrganisation,
  fetchBenchmarkCustomerContactList,
  //fetchBenchmarkInstallationList,
  deleteBenchmarkSingleContact,
} from "../overview/overview.utils";
import OverviewContactModal from "./overviewContactModal";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { headerType } from "../../widgets/table";
import path from "path";

// ---------------- PRODUCT TYPE ----------------
type Product = {
  id: number;
  title: string;
  description: string;
  image: string;
};
// ------------------------------------------------

interface InfoCardObjType {
  cardTitle: string;
  cardType: InfoCardType;
  labelType1: LabelType;
  label1: string;
  labelType2: LabelType;
  label2: string;
  labelType3: LabelType;
  label3: string;
}

const tabData = [
  {
    label: "Overview",
    path: "Overview",
  },
  {
    label: "Client Connection",
    path: "ClientConnection",
  },
  {
    label: "Invoicing",
    path: "Invoicing",
  },
  {
    label: "Scheduling",
    path: "Scheduling",
  },
  {
    label: "Quotes",
    path: "Quotes",
  },
];
const colHeaders: headerType[] = [
  {
    label: "Item",
  },
  {
    label: "Bulk Delivery",
    style: {
      alignment: { textAlign: "right" },
    },
  },
  {
    label: "Installation",
    style: {
      alignment: { textAlign: "right" },
    },
  },
];
const InfoCard: FC<{ data: InfoCardObjType }> = ({ data }) => {
  return (
    <BaseInfoCard
      cardTitle={data.cardTitle}
      cardType={data.cardType}
      details={[
        {
          labelType: data.labelType1,
          label: data.label1,
        },
        {
          labelType: data.labelType2,
          label: data.label2,
        },
        {
          labelType: data.labelType3,
          label: data.label3,
        },
      ]}
    />
  );
};

const Overview: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userId, setUserId] = useState(0);
  const [isDeleted, setIsDeleted] = useState(false);
  const accountManagerId = localStorage.getItem("AccountManagerID");
  const {
    isLoading: isLoadingBenchmarkContact,
    error: errorBenchmarkContact,
    data: dataBenchmarkContact,
  } = useQuery({
    queryKey: [BENCHMARK_CONTACT, accountManagerId],
    queryFn: () => fetchBenchmarkContact(accountManagerId),
    enabled: !!accountManagerId,
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBenchmarkOrganisation,
    error: errorBenchmarkOrganisation,
    data: dataBenchmarkOrganisation,
  } = useQuery({
    queryKey: [BENCHMARK_ORGANISATION],
    queryFn: () => fetchBenchmarkOrganisation(1),
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBenchmarkContactList,
    error: errorBenchmarkContactList,
    data: dataBenchmarkContactList,
    refetch,
  } = useQuery({
    queryKey: [BENCHMARK_CONTACTLIST],
    queryFn: () =>
      fetchBenchmarkCustomerContactList(localStorage.getItem("ID")),
    refetchOnWindowFocus: false,
  });

  // Commented, in case if needed in future
  // const {
  //   isLoading: isLoadingBenchmarkInstallationList,
  //   error: errorBenchmarkInstallationList,
  //   data: dataBenchmarkInstallationList,
  // } = useQuery({
  //   queryKey: [BENCHMARK_INSTALLATIONLIST],
  //   queryFn: () => fetchBenchmarkInstallationList(localStorage.getItem("ID")),
  //   refetchOnWindowFocus: false,
  // });

  const carouselRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const carouselInstance = new window.bootstrap.Carousel(carousel, {
        interval: 2000, // Slide every 2 seconds
        wrap: true, // Wrap around when reaching the end
      });

      return () => {
        // Cleanup function to dispose the carousel instance
        carouselInstance.dispose();
      };
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [isModalOpen]);

  const onClickAddCustomerContact = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onClickEditCustomerContact = useCallback(
    (userId: number) => {
      setIsModalOpen(true);
      setUserId(userId);
    },
    [userId]
  );

  const onClickDeleteCustomerContact = useCallback(
    (userId: number) => {
      Swal.fire({
        cancelButtonColor: "var(--onhold-color)",
        cancelButtonText: "No",
        confirmButtonColor: "var(--primary-color)",
        confirmButtonText: "Yes",
        customClass: ".swal2-cancel",
        icon: "warning",
        showCancelButton: true,
        title: "Are you sure you want to delete?",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setIsDeleted(true);

          try {
            await deleteBenchmarkSingleContact(userId);
            Swal.fire({
              icon: "success",
              text: "Contact has been deleted.",
              confirmButtonColor: "var(--primary-color)",
              title: "Deleted!",
            });
            // Optionally, refetch the contact list here if needed
            refetch();
          } catch (error) {
            console.error("Failed to delete contact", error);
            Swal.fire({
              icon: "error",
              title: "Failed to delete contact",
              text: "There was a problem deleting the contact.",
            });
          } finally {
            setIsDeleted(false); // Stop loader regardless of the outcome
          }
        }
      });
    },
    [refetch]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setUserId(0);
  }, [isModalOpen]);

  const data = useMemo(
    () => [
      {
        cardTitle: dataBenchmarkContact ? dataBenchmarkContact.NameFull : "",
        cardType: InfoCardType.Account,

        labelType1: LabelType.personal,
        label1: dataBenchmarkContact ? dataBenchmarkContact.Phone_Mobile : "",
        labelType2: LabelType.official,
        label2: dataBenchmarkContact ? dataBenchmarkContact.Phone_Office : "",
        labelType3: LabelType.email,
        label3: dataBenchmarkContact ? dataBenchmarkContact.Email_Office : "",
      },
      {
        cardTitle: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Billing_Contact
          : "",
        cardType: InfoCardType.Billing,
        labelType1: LabelType.official,
        label1: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Billing_Contact_Phone
          : "",
        labelType2: LabelType.email,
        label2: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Billing_Contact_Email
          : "",
        labelType3: LabelType.website,
        label3: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Billing_Contact_Web
          : "",
      },
      {
        cardTitle: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Owner_Contact
          : "",
        cardType: InfoCardType.Ownership,
        labelType1: LabelType.personal,
        label1: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Owner_Contact_MobilePhone
          : "",
        labelType2: LabelType.official,
        label2: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Owner_Contact_Phone
          : "",

        labelType3: LabelType.email,
        label3: dataBenchmarkOrganisation
          ? dataBenchmarkOrganisation.Company_Owner_Contact_Email
          : "",
      },
    ],
    [dataBenchmarkOrganisation, dataBenchmarkContact]
  );

  // ------------------- Product API Call -------------------
  const {
    isLoading: isLoadingProducts,
    error: errorProducts,
    data: productData,
  } = useQuery<Product[]>({
    queryKey: ["PRODUCT_LIST"],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}benchmark_api/api/products.php`);

      return res.json();
    },
    refetchOnWindowFocus: false,
  });
  // ---------------------------------------------------------

  return (
    <div>
      <div className="container-fluid">
        {isDeleted ? <Loader /> : null}
        <div className="body-content">
          <Tabs tabs={tabData} />
          {(isLoadingBenchmarkContact || isLoadingBenchmarkOrganisation) && (
            <LoaderTrans msg="Loading" />
          )}
          <div className="infoCardWrapper">
            {(errorBenchmarkContact || errorBenchmarkOrganisation) && (
              <ErrorCard msg={ERROR_MESSAGE} />
            )}
            {!isLoadingBenchmarkContact && !errorBenchmarkContact ? (
              <InfoCard data={data[0]} />
            ) : null}

            {!isLoadingBenchmarkOrganisation && !errorBenchmarkOrganisation ? (
              <InfoCard data={data[1]} />
            ) : null}
            {!isLoadingBenchmarkContactList && !errorBenchmarkContactList ? (
              <InfoCard data={data[2]} />
            ) : null}
          </div>
          <div className="d-flex contentSection align-items-stretch">
            <div className="col-md-6 d-flex flex-column">
              <Card
                cardTitle="Customer Contacts"
                cardHeaderRightContent={
                  <button
                    className="btn submitBtn"
                    onClick={onClickAddCustomerContact}
                  >
                    Add Contact
                  </button>
                }
                className="overview-product-card"
              >
                {isLoadingBenchmarkContactList && <LoaderTrans msg="Loading" />}
                {errorBenchmarkContactList && <ErrorCard msg={ERROR_MESSAGE} />}
                {dataBenchmarkContactList ? (
                  <ContactCardList
                    contacts={dataBenchmarkContactList}
                    onEdit={onClickEditCustomerContact}
                    onDelete={onClickDeleteCustomerContact}
                  />
                ) : null}
              </Card>
            </div>

        {/* RIGHT COLUMN - PRODUCTS */}
<div className="col-md-6 d-flex flex-column">
  <Card
    cardTitle="Products"
    className="overview-product-card"
  >
    {isLoadingProducts && <LoaderTrans msg="Loading Products..." />}
    {errorProducts && <ErrorCard msg="Failed to load products" />}

    {productData && (
      <div className="contact-card-list-wrapper">
        {productData.map((item: any, index: number) => (
          <div key={item.id}>
            
            {/* SINGLE ROW */}
            <div className="d-flex justify-content-between align-items-center contact-card-item">

              {/* LEFT SIDE: Title + Description */}
              <div className="overview-product-card-details">
                <div className="overview-product-name">{item.title}</div>
                <div className="overview-product-description">{item.description}</div>
              </div>

              {/* RIGHT SIDE: Fixed Circular Image */}
              <img
                src={item.image}
                alt={item.title}
                className="overview-product-avatar"
              />
            </div>

            {/* Horizontal Separator */}
            {index !== productData.length - 1 && (
              <hr className="overview-product-divider" />
            )}
          </div>
        ))}
      </div>
    )}
  </Card>
</div>


          </div>
          <div className="d-flex contentSection">
            <div className="col-md-6">
              <div className="card">
                <div className="card-body p-0">
                  <iframe
                    width="100%"
                    height="383"
                    src={RELOAD_TRUCK}
                    title="Landscape Design Basics to Improve Your Home Landscape | Prairie Yard &amp; Garden"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; loop"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-body p-0">
                  <Carousel id="myCarousel">
                    <Carousel.Item>
                      <img
                        src={Gallery1}
                        alt="Chicago"
                        className="landing-page-image"
                      />
                    </Carousel.Item>

                    <Carousel.Item>
                      <img
                        src={Gallery2}
                        alt="Chicago"
                        className="landing-page-image"
                      />
                    </Carousel.Item>
                    <Carousel.Item>
                      <img
                        src={Gallery3}
                        alt="Chicago"
                        className="landing-page-image"
                      />
                    </Carousel.Item>
                  </Carousel>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <OverviewContactModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        userId={userId}
      />
    </div>
  );
};

export default Overview;
